import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Header, PageWrapper } from "~/components";
import { InvitePatientModal } from "~/components/Patients/InvitePatientModal";
import { PatientTableFilters } from "~/components/Patients/TableFilters";
import type { ToggleOptions } from "~/components/Patients/TableFilters";
import { PatientsTable } from "~/components/Patients/Tables/PatientsTable/PatientsTable";
import { PendingTable } from "~/components/Patients/Tables/PendingTable";
import { useGetPatients, useTableSearchParams } from "~/hooks";

const PAGE_SIZE = 5;

export const MODAL_TYPE_OPTIONS = {
  EDIT: "edit",
  VIEW: "view",
  INVITE: "invite",
  DELETE: "delete",
} as const;

export type ModalType =
  (typeof MODAL_TYPE_OPTIONS)[keyof typeof MODAL_TYPE_OPTIONS];

export const Patients = () => {
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState<ModalType>();

  const {
    currentPage,
    setCurrentPage,
    searchParams,
    setSearchParams,
    search,
    setSearchText,
    sorting,
    orderBy,
    sortDirection,
    setSorting,
  } = useTableSearchParams();

  const userType = (searchParams.get("status") ??
    "active") as ToggleOptions[number]["value"];

  const setUserType = (val: ToggleOptions[number]["value"]) => {
    setSearchParams((prev) => {
      prev.set("status", val);
      prev.set("page", "1");
      prev.delete("order_by");
      prev.delete("search");
      prev.delete("sort_direction");
      return prev;
    });
  };

  const {
    patientsData,
    isLoadingPatients,
    totalPatients,
    invitationsData,
    isLoadingInvitations,
    totalInvitations,
  } = useGetPatients(
    currentPage,
    orderBy,
    search,
    sortDirection,
    userType,
    PAGE_SIZE,
  );

  const total = userType === "active" ? totalPatients : totalInvitations;

  return (
    <PageWrapper>
      <div>
        <Header
          title={t("patients.title")}
          badgeText={
            total != undefined
              ? t("patients.patient_amount", { count: total })
              : undefined
          }
          button={{
            text: t("patients.invite_patient"),
            onClick: () => {
              setModalOpen(MODAL_TYPE_OPTIONS.INVITE);
            },
          }}
        />
        <PatientTableFilters
          userType={userType}
          setUserType={setUserType}
          setSearchText={setSearchText}
        />
      </div>
      {userType === "active" && (
        <PatientsTable
          patients={patientsData?.data ?? []}
          isLoading={isLoadingPatients}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          totalItems={totalPatients}
          pageSize={PAGE_SIZE}
          sorting={sorting}
          setSorting={setSorting}
        />
      )}
      {userType === "pending" && (
        <PendingTable
          patients={invitationsData?.data ?? []}
          isLoading={isLoadingInvitations}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          totalItems={totalInvitations}
          pageSize={PAGE_SIZE}
          sorting={sorting}
          setSorting={setSorting}
        />
      )}
      <InvitePatientModal
        isOpen={modalOpen === MODAL_TYPE_OPTIONS.INVITE}
        onClose={() => setModalOpen(undefined)}
      />
    </PageWrapper>
  );
};
